import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  List as MenuIcon,
  X,
  CaretDown,
  CaretRight,
  ArrowUpRight,
} from "@phosphor-icons/react";

import { Button } from "../ui/button";
import DarkModeToggle from "@/components/ui/DarkModeToggle";
import Logo from "@/assets/logo-white.webp";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

type NavLink = {
  name: string;
  path: string;
};

type NavSubgroup = {
  name: string;
  links: NavLink[];
};

type NavGroup = {
  name: string;
  links?: NavLink[];
  subgroups?: NavSubgroup[];
};

type Notice = {
  id: string; // change this whenever you write a new notice
  icon: string; // emoji
  title: string; // bold main statement
  text: string; // supporting line
  buttonLabel: string; // what the button says
  path: string; // internal path (/students) or full URL (https://...)
};

// Edit this object to change the notice. Set to null to hide it completely.
const notice: Notice | null = {
  id: "students-welcome-2026",
  icon: "👋",
  title: "Students, welcome to Leicester!",
  text: "We can't wait to meet you.",
  buttonLabel: "Students",
  path: "/students",
};

const navGroups: NavGroup[] = [
  {
    name: "About",
    links: [
      { name: "Our Story", path: "/about" },
      { name: "Leadership", path: "/about" },
      { name: "Contact Us", path: "/contact" },
    ],
  },
  {
    name: "Worship",
    links: [
      { name: "Sundays", path: "/sundays" },
      { name: "Services", path: "/services" },
    ],
  },
  {
    name: "Connect",
    links: [
      { name: "Events", path: "/events" },
      { name: "The Area & District", path: "/district" },
    ],
    subgroups: [
      {
        name: "Departments",
        links: [
          { name: "Media", path: "/ministries/media" },
          { name: "Music", path: "/ministries/music" },
          {
            name: "Hospitality",
            path: "/ministries/hospitality",
          },
          {
            name: "Creative Arts",
            path: "/ministries/creativearts",
          },
        ],
      },
      {
        name: "People",
        links: [
          { name: "Men", path: "/ministries/men" },
          { name: "Youth", path: "/ministries/youth" },
          { name: "Women", path: "/ministries/women" },
          {
            name: "Kids",
            path: "/ministries/kids",
          },
          {
            name: "Students",
            path: "/students",
          },
          {
            name: "Evangelism",
            path: "/ministries/evangelism",
          },
        ],
      },
    ],
  },
  {
    name: "Media",
    links: [
      { name: "Sermons", path: "/sermons" },
      { name: "Tales of the Called", path: "/totc" },
      { name: "Discipleship Series", path: "/discipleship" },
    ],
  },
  {
    name: "Give",
    links: [
      { name: "Donate", path: "/donations" },
      { name: "Food Bank", path: "/foodbank" },
      { name: "Gift Aid", path: "/gift-aid" },
    ],
  },
];

const linkVariants = {
  hidden: { opacity: 0, x: 16 },
  visible: (index: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: index * 0.035, duration: 0.22 },
  }),
};

const SCROLL_THRESHOLD = 24;

// Same channel and cache key as your LiveStream page, so both share one cache.
const CHANNEL_ID = "UCEirtQPI4vk8jUgMNBiIoaw";
const LIVE_CACHE_KEY = "live_stream_cache";
const LIVE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const LIVE_CHECK_INTERVAL = 60 * 1000; // look at the cache every minute

const isExternal = (path: string) => /^https?:\/\//i.test(path);

/**
 * Returns true only while a live stream is broadcasting.
 * It reads the shared cache first and only calls the API when the cache is
 * older than 5 minutes, so the header doesn't spam your YouTube quota.
 */
const useIsLive = () => {
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // 1. Fresh cache? Use it and skip the network.
      try {
        const cached = localStorage.getItem(LIVE_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (
            parsed &&
            typeof parsed.timestamp === "number" &&
            Date.now() - parsed.timestamp < LIVE_CACHE_DURATION
          ) {
            if (!cancelled) setIsLive(typeof parsed.data?.videoId === "string");
            return;
          }
        }
      } catch {
        // corrupted or blocked cache: fall through to the network
      }

      // 2. Cache is stale or missing: ask the API.
      try {
        const res = await fetch(`/api/youtube/live?channelId=${CHANNEL_ID}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        const video = json.items?.[0];

        const data = video
          ? {
              videoId: video.id.videoId as string,
              title: video.snippet?.title ?? "",
              thumbnail: video.snippet?.thumbnails?.high?.url ?? "",
              viewers: video.liveStreamingDetails?.concurrentViewers
                ? Number(video.liveStreamingDetails.concurrentViewers)
                : null,
            }
          : null;

        // Save in the exact shape LiveStream.tsx expects.
        try {
          localStorage.setItem(
            LIVE_CACHE_KEY,
            JSON.stringify({ data, timestamp: Date.now() }),
          );
        } catch {
          // ignore: storage full or disabled
        }

        if (!cancelled) setIsLive(!!data);
      } catch {
        // If the check fails, hide the pill rather than show a false "live".
        if (!cancelled) setIsLive(false);
      }
    };

    check();
    const interval = setInterval(check, LIVE_CHECK_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return isLive;
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isLive = useIsLive();

  const noticeKey = notice ? `notice-dismissed-${notice.id}` : "";

  const [noticeDismissed, setNoticeDismissed] = useState(() => {
    if (!notice) return true;
    try {
      return localStorage.getItem(noticeKey) === "true";
    } catch {
      return false;
    }
  });

  const location = useLocation();

  // Transparent at the top of every page, unless the mobile menu is open.
  const transparent = !scrolled && !isMenuOpen;

  // The notice is hidden while the full-screen mobile menu is open.
  const showNotice = !!notice && !noticeDismissed && !isMenuOpen;

  const dismissNotice = () => {
    setNoticeDismissed(true);
    try {
      localStorage.setItem(noticeKey, "true");
    } catch {
      // ignore: the notice will simply reappear on the next visit
    }
  };

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Stop the page behind the menu from scrolling while the menu is open.
  useEffect(() => {
    if (!isMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  const isActive = (path: string) =>
    location.pathname === path ||
    (path !== "/" && location.pathname.startsWith(`${path}/`));

  const isGroupActive = (group: NavGroup) =>
    [
      ...(group.links ?? []),
      ...(group.subgroups ?? []).flatMap((subgroup) => subgroup.links),
    ].some((item) => isActive(item.path));

  const closeMenu = () => setIsMenuOpen(false);

  const desktopNavClass = (active: boolean) =>
    `group relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors
     focus-visible:outline-none focus-visible:ring-2
     after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5
     after:origin-center after:rounded-full after:content-['']
     after:transition-transform after:duration-200
     ${
       transparent
         ? `focus-visible:ring-white after:bg-white ${
             active
               ? "text-white after:scale-x-100"
               : "text-white/85 after:scale-x-0 hover:text-white hover:after:scale-x-100"
           }`
         : `focus-visible:ring-primary after:bg-primary ${
             active
               ? "text-primary after:scale-x-100"
               : "text-foreground/80 after:scale-x-0 hover:text-primary hover:after:scale-x-100"
           }`
     }`;

  const dropdownLinkClass = (active: boolean) =>
    `flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 transition-colors hover:bg-muted hover:text-primary ${
      active ? "font-semibold text-primary" : "text-foreground"
    }`;

  const mobileLinkClass = (active: boolean) =>
    `relative block rounded-lg px-3 py-2.5 text-sm transition-colors
     after:absolute after:bottom-1 after:left-3 after:right-3 after:h-0.5
     after:origin-left after:bg-primary after:content-['']
     after:transition-transform after:duration-200
     ${
       active
         ? "font-semibold text-primary after:scale-x-100"
         : "text-foreground/80 after:scale-x-0 hover:bg-muted hover:text-primary hover:after:scale-x-100"
     }`;

  const lookClass = transparent
    ? "border-transparent bg-transparent"
    : "border-border/50 bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/75";

  return (
    <>
      <header
        className={`fixed left-0 top-0 w-full border-b transition-colors duration-300 ${
          isMenuOpen ? "z-[70]" : "z-50"
        } ${lookClass}`}
      >
        {/* Notice bar */}
        {showNotice && notice && (
          <div className="border-b border-black/5 bg-primary text-white">
            <div className="container mx-auto flex items-center gap-3 px-4 py-3">
              <span className="text-2xl" aria-hidden="true">
                {notice.icon}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold sm:text-base">{notice.title}</p>
                <p className="text-xs sm:text-sm">{notice.text}</p>
              </div>

              <Button
                asChild
                size="sm"
                className="shrink-0 rounded-lg bg-secondary text-primary hover:bg-secondary/90"
              >
                {isExternal(notice.path) ? (
                  <a
                    href={notice.path}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {notice.buttonLabel}
                  </a>
                ) : (
                  <Link to={notice.path}>{notice.buttonLabel}</Link>
                )}
              </Button>

              <button
                type="button"
                onClick={dismissNotice}
                aria-label="Dismiss notice"
                className="shrink-0 rounded-full p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
          </div>
        )}

        <div className="container mx-auto px-4">
          <div className="flex h-[76px] items-center justify-between gap-5">
            {/* Logo + LIVE pill (pill only exists while a stream is live) */}
            <div className="flex items-center gap-3">
              <Link
                to="/"
                aria-label="PIWC Leicester home"
                className="flex shrink-0 items-center"
              >
                <img
                  src={Logo}
                  alt="PIWC Leicester"
                  className={`h-11 w-auto sm:h-12 ${
                    transparent ? "" : "invert dark:invert-0"
                  }`}
                />
              </Link>

              {isLive && (
                <Link
                  to="/live"
                  aria-label="Watch the live stream"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-red-700"
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                  Live
                </Link>
              )}
            </div>

            {/* Desktop navigation */}
            <nav
              aria-label="Main navigation"
              className="hidden min-w-0 flex-1 items-center justify-end gap-1 xl:flex"
            >
              {navGroups.map((group) => {
                const active = isGroupActive(group);

                return (
                  <DropdownMenu key={group.name}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={desktopNavClass(active)}
                        aria-label={`${group.name} menu`}
                      >
                        <span>{group.name}</span>
                        <CaretDown
                          size={14}
                          weight="bold"
                          className="transition-transform group-data-[state=open]:rotate-180"
                          aria-hidden="true"
                        />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="start"
                      className="w-64 rounded-xl border border-border/70 bg-background p-2 shadow-xl"
                    >
                      {(group.links ?? []).map((item) => (
                        <DropdownMenuItem
                          key={`${group.name}-${item.name}`}
                          asChild
                        >
                          <Link
                            to={item.path}
                            className={dropdownLinkClass(isActive(item.path))}
                          >
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}

                      {(group.subgroups ?? []).map((subgroup) => (
                        <DropdownMenuSub key={subgroup.name}>
                          <DropdownMenuSubTrigger className="rounded-lg px-3 py-2.5">
                            {subgroup.name}
                          </DropdownMenuSubTrigger>

                          <DropdownMenuSubContent className="w-60 rounded-xl border border-border/70 bg-background p-2 shadow-xl">
                            {subgroup.links.map((item) => (
                              <DropdownMenuItem
                                key={`${subgroup.name}-${item.name}`}
                                asChild
                              >
                                <Link
                                  to={item.path}
                                  className={dropdownLinkClass(
                                    isActive(item.path),
                                  )}
                                >
                                  {item.name}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}

              <Button
                asChild
                className={`ml-2 h-10 rounded-full px-5 font-semibold shadow-sm transition-colors ${
                  transparent
                    ? "bg-white text-neutral-950 hover:bg-white/90"
                    : ""
                }`}
              >
                <Link to="/join">
                  Plan Your Visit
                  <ArrowUpRight
                    size={16}
                    weight="bold"
                    className="ml-1.5"
                    aria-hidden="true"
                  />
                </Link>
              </Button>

              <div
                className={`ml-1 ${
                  transparent
                    ? "text-white [&_button]:text-white [&_svg]:text-white"
                    : ""
                }`}
              >
                <DarkModeToggle />
              </div>
            </nav>

            {/* Mobile header controls */}
            <div className="flex items-center gap-2 xl:hidden">
              <div
                className={
                  transparent
                    ? "text-white [&_button]:text-white [&_svg]:text-white"
                    : ""
                }
              >
                <DarkModeToggle />
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen((open) => !open)}
                className={`rounded-full ${
                  transparent
                    ? "text-white hover:bg-white/15 hover:text-white"
                    : ""
                }`}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-navigation"
              >
                {isMenuOpen ? (
                  <X size={24} weight="bold" />
                ) : (
                  <MenuIcon size={24} weight="bold" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Full-screen mobile navigation.
          It lives OUTSIDE the <header> on purpose (see notes below). */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            key="mobile-navigation"
            id="mobile-navigation"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.24, ease: "easeOut" }}
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-background px-5 pb-10 pt-24 sm:px-8"
          >
            <nav
              aria-label="Mobile navigation"
              className="mx-auto flex w-full max-w-3xl flex-col"
            >
              {navGroups.map((group, index) => {
                const active = isGroupActive(group);

                return (
                  <motion.div
                    key={group.name}
                    custom={index}
                    initial="hidden"
                    animate="visible"
                    variants={linkVariants}
                    className="border-b border-border/60"
                  >
                    <Collapsible defaultOpen={active}>
                      <CollapsibleTrigger
                        className={`flex w-full items-center justify-between py-4 text-left text-lg font-semibold transition-colors hover:text-primary ${
                          active ? "text-primary" : "text-foreground"
                        }`}
                      >
                        {group.name}
                        <CaretDown
                          size={18}
                          weight="bold"
                          className="transition-transform data-[state=open]:rotate-180"
                          aria-hidden="true"
                        />
                      </CollapsibleTrigger>

                      <CollapsibleContent className="pb-3">
                        <div className="ml-1 space-y-1 border-l-2 border-primary/20 pl-4">
                          {(group.links ?? []).map((item) => (
                            <Link
                              key={`${group.name}-${item.name}`}
                              to={item.path}
                              onClick={closeMenu}
                              className={mobileLinkClass(isActive(item.path))}
                            >
                              {item.name}
                            </Link>
                          ))}

                          {(group.subgroups ?? []).map((subgroup) => (
                            <Collapsible
                              key={subgroup.name}
                              defaultOpen={subgroup.links.some((item) =>
                                isActive(item.path),
                              )}
                            >
                              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-sm font-semibold text-foreground transition-colors hover:text-primary">
                                {subgroup.name}
                                <CaretRight
                                  size={16}
                                  weight="bold"
                                  className="transition-transform data-[state=open]:rotate-90"
                                  aria-hidden="true"
                                />
                              </CollapsibleTrigger>

                              <CollapsibleContent className="pb-2 pl-3">
                                <div className="space-y-1 border-l border-primary/20 pl-3">
                                  {subgroup.links.map((item) => (
                                    <Link
                                      key={`${subgroup.name}-${item.name}`}
                                      to={item.path}
                                      onClick={closeMenu}
                                      className={mobileLinkClass(
                                        isActive(item.path),
                                      )}
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </motion.div>
                );
              })}

              <Button
                asChild
                size="lg"
                className="mt-6 h-12 w-full rounded-full text-base font-semibold"
              >
                <Link to="/join" onClick={closeMenu}>
                  Plan Your Visit
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="ml-2"
                    aria-hidden="true"
                  />
                </Link>
              </Button>

              <Link
                to="/contact"
                onClick={closeMenu}
                className="mt-4 py-3 text-center text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                Need help? Contact us
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;